import {
  type LabSim,
  PHET_SITE,
  labSim,
  simUrl,
  useCourses,
  useMaterialActions,
} from '@shkola/api';
import { ArrowsOutIcon } from '@phosphor-icons/react/ArrowsOut';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  Block,
  GRAD,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  grad,
  pillClass,
} from '../design/forms';
import { CheckIcon, PlusIcon } from '../design/icons';
import { FitChips } from './LabPage';

/*
 * A simulation of the lab in «Форми 2» (28.10): back to the lab as a small pill, the name on the paper with
 * what it shows and where it fits; the simulation in a white block, «На весь екран» as a light pill; beside
 * it «Спробуй» — small experiments ticked off in round marks — and, for the teacher, «До матеріалів курсу».
 */

/** Back to the lab: a small light pill above the title. */
export function BackToLab({ tab, children }: { tab: 'sims' | 'models'; children: ReactNode }) {
  return (
    <Link to="/lab" search={{ tab }} className={`${pillClass('light', 'sm')} self-start pr-3.5`}>
      {children}
    </Link>
  );
}

/** The teacher puts the simulation into the materials of one of their courses. */
function AddToCourse({ sim, teacherId }: { sim: LabSim; teacherId: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const courses = useCourses({ teacherMemberId: teacherId });
  const actions = useMaterialActions(ctx.schoolId, ctx.memberId);
  const subjects = new Set(sim.fits.map(([s]) => s));
  const list = courses.data ?? [];
  // the courses of the simulation's subjects first
  const sorted = [...list].sort(
    (a, b) => Number(subjects.has(b.subject)) - Number(subjects.has(a.subject)),
  );
  const [courseId, setCourseId] = useState('');
  const course = courseId || sorted[0]?.id || '';
  const [done, setDone] = useState<string>();
  if (list.length === 0) return null;
  return (
    <div className="flex flex-col gap-2.5 rounded-[20px] p-4" style={{ backgroundColor: PAPER }}>
      <p className="text-[15px] font-medium" style={{ color: INK }}>
        {t('lab.add.title')}
      </p>
      <select
        aria-label={t('lab.add.course')}
        value={course}
        onChange={(e) => {
          setCourseId(e.target.value);
          setDone(undefined);
        }}
        className="h-11 w-full rounded-[14px] bg-white px-3 text-[14px] text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] outline-none ring-inset focus:ring-2 focus:ring-[var(--sh-dark)]"
      >
        {sorted.map((c) => (
          <option key={c.id} value={c.id}>
            {c.subject}
            {c.groupLabel ? ` (${c.groupLabel})` : ''} · {c.classLabel}
          </option>
        ))}
      </select>
      {done === course ? (
        <p
          className="self-start rounded-[8px] px-2.5 py-1.5 text-[13px] font-medium"
          style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
        >
          {t('lab.add.done')}
        </p>
      ) : (
        <button
          type="button"
          disabled={actions.addLink.isPending}
          onClick={() =>
            void actions.addLink
              .mutateAsync({ courseId: course, title: sim.title, url: simUrl(sim.slug) })
              .then(() => setDone(course))
          }
          className={`${pillClass('dark')} self-start disabled:opacity-35`}
        >
          {t('lab.add.button')}
          <PillIcon icon={PlusIcon} />
        </button>
      )}
    </div>
  );
}

/** «Спробуй»: small experiments to tick off; remembered only while the page is open. */
export function Tries({ sim, head = true }: { sim: LabSim; head?: boolean }) {
  const { t } = useTranslation();
  const [done, setDone] = useState<ReadonlySet<number>>(new Set());
  return (
    <div>
      {head && (
        <p className="mb-2.5 text-[15px] font-medium" style={{ color: INK }}>
          {t('lab.tries')}
        </p>
      )}
      <ul className="flex flex-col gap-1.5">
        {sim.tries.map((text, i) => {
          const on = done.has(i);
          return (
            <li key={text}>
              <label
                className="flex cursor-pointer gap-3 rounded-[16px] p-3 text-[14.5px] leading-[21px] transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--sh-dark)]"
                style={on ? { backgroundImage: grad('lime') } : { backgroundColor: PAPER }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const next = new Set(done);
                    if (!next.delete(i)) next.add(i);
                    setDone(next);
                  }}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full text-white"
                  style={
                    on
                      ? { backgroundColor: 'var(--sh-dark)' }
                      : { backgroundColor: '#FFFFFF', boxShadow: 'inset 0 0 0 1.5px #E4E0D7' }
                  }
                >
                  {on && <CheckIcon size={11} weight="bold" />}
                </span>
                <span className={on ? 'line-through' : ''} style={{ color: on ? INK_SOFT : INK }}>
                  {text}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function SimFrame({ sim, className = '' }: { sim: LabSim; className?: string }) {
  return (
    <iframe
      title={sim.title}
      src={simUrl(sim.slug)}
      allow="fullscreen"
      className={`size-full border-0 bg-white ${className}`}
    />
  );
}

/** The credit PhET asks for (CC BY 4.0), quiet. */
function Credit() {
  const { t } = useTranslation();
  return (
    <p className="text-[12px] leading-[17px]" style={{ color: INK_FAINT }}>
      {t('lab.credit')}{' '}
      <a href={PHET_SITE} target="_blank" rel="noreferrer" className="underline">
        phet.colorado.edu
      </a>
    </p>
  );
}

export function SimPage({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const sim = labSim(slug);
  const frame = useRef<HTMLDivElement>(null);
  if (!sim) {
    return (
      <div className="flex flex-col gap-4">
        <BackToLab tab="sims">{t('lab.back')}</BackToLab>
        <Block>
          <p className="text-[15px]" style={{ color: INK_SOFT }}>
            {t('lab.notFound')}
          </p>
        </Block>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <BackToLab tab="sims">{t('lab.back')}</BackToLab>
        <PageHead
          title={sim.title}
          sub={
            <span className="flex flex-col gap-2.5">
              <span className="text-[15px]">{sim.about}</span>
              <FitChips fits={sim.fits} />
            </span>
          }
        />
      </div>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="flex min-w-0 flex-col rounded-[28px] bg-white p-2.5 md:p-3">
          <div
            ref={frame}
            className="aspect-[16/10] max-h-[72vh] w-full overflow-hidden rounded-[20px]"
            style={{ backgroundColor: PAPER }}
          >
            <SimFrame sim={sim} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-2 pt-3 pb-1">
            <p className="text-[13px]" style={{ color: INK_FAINT }}>
              {t('lab.online')}
            </p>
            <button
              type="button"
              onClick={() => void frame.current?.requestFullscreen?.()}
              className={pillClass('light', 'sm')}
            >
              {t('lab.fullscreen')}
              <PillIcon icon={ArrowsOutIcon} size="sm" />
            </button>
          </div>
        </section>
        <Block className="gap-5">
          <div>
            <Head title={t('lab.tries')} count={sim.tries.length} />
            <Tries sim={sim} head={false} />
          </div>
          {ctx.kind === 'teacher' && <AddToCourse sim={sim} teacherId={ctx.memberId} />}
          <Credit />
        </Block>
      </div>
    </div>
  );
}
